/**
 * 管理员登录态：token、当前管理员信息、权限判断。
 * 能力对应旧管理中台 stores/admin.ts；持久化 key 换成新的（acquilang-admin-auth），
 * 切换上线后管理员需要重新登录一次（迁移计划 KTD6）。
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { request } from '@/api/client'
import { getAdminApiUrl } from '@/utils/env'
import { AdminRole, ROLE_NAMES, type AdminError, type AdminUser, type LoginCredentials, type LoginResponse } from '@/types/admin'

interface AuthState {
  user: AdminUser | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean
  isInitialized: boolean
  // 进行中的初始化；登录页和主布局的守卫可能同时触发，共用同一次
  pendingInitialize: Promise<void> | null
  error: AdminError | null

  login: (credentials: LoginCredentials) => Promise<boolean>
  logout: () => Promise<void>
  refreshAuthToken: () => Promise<boolean>
  validateToken: () => Promise<boolean>
  // 启动时校验已保存的 token，失效则尝试刷新，刷新失败清空登录态
  initialize: () => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>
  clearAuth: () => void

  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
}

function toAdminError(err: any, fallbackCode: string, fallbackMessage: string): AdminError {
  return {
    code: err.response?.data?.error_code || fallbackCode,
    message: err.response?.data?.message || err.message || fallbackMessage,
    timestamp: new Date().toISOString()
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      isInitialized: false,
      pendingInitialize: null,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null })
        try {
          const response = await request.post<LoginResponse>(getAdminApiUrl('/auth/login'), credentials)
          // 登录接口直接返回 JWT 格式（不是 success/data 包装格式）
          const responseData = response.data as any
          if (!responseData.access_token) throw new Error(responseData.message || '登录失败')

          // 后端只返回一个 token，刷新时也用它
          set({ user: responseData.user, token: responseData.access_token, refreshToken: responseData.access_token })
          return true
        } catch (err: any) {
          console.error('登录失败:', err)
          set({ error: toAdminError(err, 'LOGIN_FAILED', '登录失败') })
          return false
        } finally {
          set({ isLoading: false })
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          await request.post(getAdminApiUrl('/auth/logout'))
        } catch (err) {
          // 登出接口失败也要清除本地状态
          console.error('登出API调用失败:', err)
        } finally {
          get().clearAuth()
          set({ isLoading: false })
        }
      },

      refreshAuthToken: async () => {
        const refreshToken = get().refreshToken
        if (!refreshToken) return false

        try {
          const response = await request.post<LoginResponse>(getAdminApiUrl('/auth/refresh-token'), {
            refresh_token: refreshToken
          })
          const responseData = response.data as any
          if (responseData.access_token) {
            set({ user: responseData.user, token: responseData.access_token, refreshToken: responseData.access_token })
            return true
          }
        } catch (err) {
          console.error('刷新token失败:', err)
          get().clearAuth()
        }
        return false
      },

      validateToken: async () => {
        if (!get().token) return false

        try {
          const response = await request.get<AdminUser>(getAdminApiUrl('/auth/me'))
          // /auth/me 可能返回包装格式，也可能直接返回用户对象
          const responseData = response.data as any
          if (responseData.success && responseData.data) {
            set({ user: responseData.data })
            return true
          }
          if (responseData.id) {
            set({ user: responseData })
            return true
          }
        } catch (err) {
          // 不在这里清空：initialize 还要用 refreshToken 尝试刷新
          // （旧版在这里清空，导致之后的刷新永远拿不到 refreshToken）
          console.error('验证token失败:', err)
        }
        return false
      },

      initialize: async () => {
        if (get().isInitialized) return
        const pending = get().pendingInitialize
        if (pending) return pending

        // 放到微任务里执行，保证下面的 set 先记下 run
        const run: Promise<void> = Promise.resolve().then(async () => {
          set({ isLoading: true })
          try {
            const { token, user } = get()
            if (token && user) {
              const isValid = await get().validateToken()
              if (!isValid) {
                const refreshed = await get().refreshAuthToken()
                if (!refreshed) get().clearAuth()
              }
            }
          } catch (err) {
            console.error('初始化认证状态失败:', err)
            get().clearAuth()
          } finally {
            // 期间登录态被整体重置过（如测试或登出）时，不覆盖新的状态
            if (get().pendingInitialize === run) {
              set({ isInitialized: true, isLoading: false, pendingInitialize: null })
            }
          }
        })
        set({ pendingInitialize: run })
        return run
      },

      changePassword: async (oldPassword, newPassword) => {
        set({ isLoading: true, error: null })
        try {
          await request.put(getAdminApiUrl('/auth/change-password'), {
            old_password: oldPassword,
            new_password: newPassword
          })
          return true
        } catch (err: any) {
          console.error('修改密码失败:', err)
          set({ error: toAdminError(err, 'PASSWORD_CHANGE_FAILED', '密码修改失败') })
          return false
        } finally {
          set({ isLoading: false })
        }
      },

      clearAuth: () => set({ user: null, token: null, refreshToken: null, error: null }),

      hasPermission: (permission) => {
        const user = get().user
        if (!user) return false
        // 超级管理员拥有所有权限
        if (user.role === AdminRole.SUPER_ADMIN) return true
        return user.permissions.includes(permission)
      },

      hasRole: (role) => get().user?.role === role,

      hasAnyRole: (roles) => roles.includes(get().user?.role ?? '')
    }),
    {
      name: 'acquilang-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user, token: state.token, refreshToken: state.refreshToken })
    }
  )
)

export function selectIsAuthenticated(state: Pick<AuthState, 'user' | 'token'>): boolean {
  return !!state.user && !!state.token
}

// 以下为 Header 等处展示用的派生值（对应旧 stores/admin.ts 的计算属性）
export function getUserDisplayName(user: AdminUser | null): string {
  return user?.display_name || user?.username || '管理员'
}

// 中文名取第一个字，英文名取首字母
export function getUserInitials(user: AdminUser | null): string {
  const name = getUserDisplayName(user)
  if (name.length <= 2) return name.toUpperCase()
  if (/^[一-龥]/.test(name)) return name.charAt(0)
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function getRoleDisplayName(role: string | undefined): string {
  return (role && ROLE_NAMES[role as AdminRole]) || role || ''
}
